import React from "react";
import {
  Card,
  CardHeader,
  Breadcrumbs as MuiBreadcrumbs,
  Typography,
  Paper as MuiPaper,
  CardContent,
  Box,
} from "@mui/material";
import styled from "@emotion/styled";
import { spacing } from "@mui/system";
import { DataGrid } from "@mui/x-data-grid";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { useDialog } from "../../../contexts/DialogContext";
import Collect from "../../invoices/unpaid_invoices/components/Collect";
import EditDelete from "../../../components/EditDelete";
import Toolbar from "../../../components/Toolbar";
import useWindowDimensions from "../../../utils/windowDimensions";
import AdvanceFilter from "./AdvanceFilter";
import useReport from "../../../hooks/useReports";
import { getTimeStamp } from "../../../utils/common";
import useMainContentDimensions from "../../../utils/mainContentDimensions";

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Paper = styled(MuiPaper)(spacing);

const breadCrump = () => {
  return (
    <Breadcrumbs aria-label="Breadcrumb" mt={2}>
      <Typography>Reports</Typography>
      <Typography>Due Reports</Typography>
    </Breadcrumbs>
  );
};

const CustomDataGrid = (props) => {
  const { isLoadingDownload, downloadFileType } = useReport();
  const { width: mainContentWidth } = useMainContentDimensions();

  const { height, width } = useWindowDimensions();
  const navigate = useNavigate();
  const {
    trans,
    data,
    rowCount,
    paging,
    payload,
    setPayload,
    setFilterResetFunc,
    onPaginationModelChange,
    isLoading,
    id,
    handleSortModelChange,
    sortModel,
    downloadFile,
    openDownloadDialog,
    refreshPage,
    filterFormData,
    setFilterFormData,
  } = props;
  console.log(rowCount, "loading child component");
  const { openDialog } = useDialog();

  const collectClick = (id) => {
    return openDialog(
      "collect",
      "Collect",
      <Collect key={getTimeStamp()} renewId={id} refreshPage={refreshPage} />,
    );
  };

  const columns = [
    {
      field: "action",
      headerName: "Action",
      filter: false,
      sortable: false,
      renderCell: (params) => {
        const onClick = (e) => {
          e.stopPropagation(); // don't select this row after clicking
          const api = params.api;
          const thisRow = {};
          api
            .getAllColumns()
            .filter((c) => c.field !== "__check__" && !!c)
            .forEach((c) => (thisRow[c.field] = params.getValue(params.id, c.field)));
          console.log(thisRow, "thisRow");
        };
        return (
          <EditDelete
            rowClick={onClick}
            clickIcon={() => viewInvoice(params.id)}
            collectClick={() => collectClick(params.id)}
            menus={["invoice_view", "collect"]}
          />
        );
      },
    },
    {
      field: "invoiceNo",
      headerName: "Invoice No",
    },
    {
      field: "userName",
      headerName: "User Name",
      width: 300,
    },
    {
      field: "fullName",
      headerName: "User Full Name",
      width: 300,
    },
    {
      field: "planName",
      headerName: "Plan Name",
      width: 300,
    },
    {
      field: "rateFullName",
      headerName: "Rate Name",
      width: 300,
    },
    {
      field: "address",
      headerName: "Address",
      width: 300,
    },
    {
      field: "tenantName",
      headerName: "Franchise Name",
      width: 300,
    },

    {
      field: "mobileNo",
      headerName: "Mobile No",
      width: 130,
    },
    {
      field: "renewDate",
      headerName: "Renew Date",
      width: 200,
    },
    {
      field: "invoiceDate",
      headerName: "Invoice Date",
      width: 200,
    },
    {
      field: "serviceTaxAmount",
      headerName: "Service Tax",
      width: 130,
    },
    {
      field: "discountAmount",
      headerName: "Discount",
      width: 100,
    },
    {
      field: "planAmount",
      headerName: "Plan Amount",
      width: 130,
    },
    {
      field: "finalInvoiceAmount",
      headerName: "Total Payable Amount",
      width: 200,
    },
    {
      field: "invoiceStatus",
      headerName: "Invoice Status",
      width: 130,
    },
  ];

  const openFilterDialog = () => {
    return openDialog(
      "advance_filter",
      "Advance Filter",
      <AdvanceFilter
        payload={payload}
        setPayload={setPayload}
        setFilterResetFunc={setFilterResetFunc}
        filterFormData={filterFormData}
        setFilterFormData={setFilterFormData}
      />,
    );
  };

  const viewInvoice = (id) => {
    navigate("/user/invoice", { state: { id: id } });
  };
  return (
    <Card sx={{ width: "100%", maxWidth: mainContentWidth - 64 }}>
      <CardHeader
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" }, // Stack on small screens
          justifyContent: "space-between",
          alignItems: "flex-start", // Adjust alignment as needed
        }}
        action={
          <Toolbar
            downloadFile={downloadFile}
            openDownloadDialog={openDownloadDialog}
            refreshPage={refreshPage}
            openFilterDialog={openFilterDialog}
            isLoadingDownload={isLoadingDownload}
            downloadFileType={downloadFileType}
          />
        }
        title={
          <Typography variant="h6" sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}>
            {trans("due_reports")}
          </Typography>
        }
        subheader={<Box sx={{ display: "flex", flexWrap: "wrap" }}>{breadCrump()}</Box>}
      />
      <CardContent sx={{ height: height - 180, display: "flex", flexDirection: "column" }}>
        <Box sx={{ flex: 1, overflow: "auto" }}>
          <DataGrid
            columns={columns}
            rows={data}
            rowCount={rowCount}
            getRowId={(row) => row[id]}
            loading={isLoading}
            rowsPerPageOptions={[5]}
            pagination
            page={paging.pageNumber}
            pageSize={paging.pageSize}
            paginationMode="server"
            onPaginationModelChange={onPaginationModelChange}
            onPageSizeChange={(pageSize) => onPageChange(pageSize)}
            sortingMode="server"
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row"
            }
            sx={{
              "& .MuiDataGrid-row.even-row": {
                backgroundColor: "#f9f9f9", // Light gray for even rows
              },
              "& .MuiDataGrid-row.odd-row": {
                backgroundColor: "#ffffff", // White for odd rows
              },
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};
CustomDataGrid.propTypes = {
  trans: PropTypes.func.isRequired,
  data: PropTypes.array.isRequired,
  rowCount: PropTypes.number.isRequired,
  paging: PropTypes.object.isRequired,
  onPageChange: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  id: PropTypes.string.isRequired,
  handleSortModelChange: PropTypes.func.isRequired,
  sortModel: PropTypes.object.isRequired,
};
export default CustomDataGrid;
